import { el, clear, toast } from '../dom';
import { progressionApi } from '../game-api';
import { ApiError } from '../../core/network/api';
import { dispatchAction, isDropped } from '../action/dispatch';
import type { GameCtx, ViewTeardown } from '../types';
import { gicon, richText, stripEmoji } from '../ui/icons';
import { playSfx } from '../audio/audio';
import {
  type QuestRow, groupQuests, summarise, progressPct, cappedProgress, isClaimable, isClaimed,
  msUntilReset, fmtCountdown, difficulty, questIcon, DIFFICULTY_LABEL, DIFFICULTY_COLOR,
} from './quests/quest-logic';

/**
 * The quest board.
 *
 * Was a flat grid of grey cards with a hairline bar and a text blob. It is now the
 * same plank/gold chrome as the shop and the arena (2px #5a4632 frame, #322820
 * header, `.game-font`), grouped into Daily / Weekly boards, each board carrying a
 * live countdown to its reset.
 *
 * Everything shown comes off `GET /api/progression/quests`. The endpoint returns
 * the whole `UserQuest` row plus its `quest`, so name/description/type/progress/
 * target/rewards/action are all real. Difficulty is DERIVED from the reward size
 * (see quest-logic) because there is no tier column, and the reset countdowns come
 * from the backend's own cron schedule (00:00 UTC daily, Monday 00:00 UTC weekly)
 * because `UserQuest` has no expiry column. Nothing else is displayed.
 *
 * Mobile-first: one column under `sm`, every button ≥44px tall, no state that is
 * reachable only by hover.
 */

const PANEL = 'game-font rounded-xl border-2 border-[#5a4632] bg-surface/95 shadow-lift overflow-hidden';
const PLANK = 'game-font rounded-lg border-2 border-[#5a4632] bg-[#322820]';

function panelHeader(...children: (Node | string | null)[]): HTMLElement {
  return el('header', 'px-3 sm:px-4 py-2.5 border-b-2 border-[#5a4632] bg-[#322820] text-[#ffd97a] text-sm flex flex-wrap items-center gap-x-3 gap-y-1',
    {}, ...children.filter((c): c is Node | string => c != null));
}

export function renderQuests(root: HTMLElement, ctx: GameCtx): ViewTeardown {
  clear(root);

  const summaryHost = el('div', 'mb-4');
  const boardsHost = el('div', 'flex flex-col gap-4 mb-8');

  root.append(
    summaryHost,
    boardsHost,
  );

  let quests: QuestRow[] = [];
  /** Countdown nodes repainted every second: [node, questType]. Rebuilt on paint. */
  let timers: [HTMLElement, string][] = [];

  // ─── Summary ──────────────────────────────────────────────────────────────
  function paintSummary() {
    const s = summarise(quests);
    clear(summaryHost);
    if (!s.total) return;
    const pct = progressPct(s.done, s.total);
    summaryHost.append(el('div', `${PLANK} px-4 py-3 flex flex-col gap-2`, {},
      el('div', 'flex flex-wrap items-center gap-x-4 gap-y-1', {},
        el('span', 'text-[#ffd97a] text-sm', {}, 'Quest board'),
        el('span', 'text-xs text-text-dim font-mono tabular-nums', {}, `${s.done} / ${s.total} done`),
        s.claimable
          ? el('span', 'text-xs text-leaf inline-flex items-center gap-1', {},
              gicon('check', 11), `${s.claimable} ready to claim`)
          : el('span', 'text-xs text-text-dim', {}, 'nothing to claim yet')),
      el('div', 'h-2 rounded-full bg-[#1b120c] border border-[#5a4632] overflow-hidden', {},
        el('div', 'h-full bg-gold', { style: `width:${pct}%` })),
      s.claimable
        ? el('div', 'text-[11px] font-mono text-coin inline-flex items-center gap-1 flex-wrap', {},
            ...richText(`Waiting for you: 🪙 ${s.coinsWaiting.toLocaleString()} · ⭐ ${s.xpWaiting.toLocaleString()} XP`, 11))
        : null));
  }

  // ─── One quest row ────────────────────────────────────────────────────────
  function questRow(q: QuestRow): HTMLElement {
    const target = q.quest.targetCount;
    const cur = cappedProgress(q);
    const pct = progressPct(q.progress, target);
    const claimable = isClaimable(q);
    const claimed = isClaimed(q);
    const diff = difficulty(q);
    const tone = DIFFICULTY_COLOR[diff];

    // Emblem: the tracked action, framed like an item slot so the row scans at a
    // glance without reading the description.
    const emblem = el('div', 'shrink-0 rounded-md flex items-center justify-center', {
      style: `width:44px;height:44px;background:radial-gradient(circle at 50% 38%, #3a2a1b, #241810);`
        + `border:2px solid ${claimed ? '#4a3524' : tone};`
        + (claimable ? `box-shadow:0 0 10px ${tone}66;` : '')
        + (claimed ? 'filter:grayscale(1) brightness(.7);' : ''),
    }, gicon(questIcon(q), 22));

    const bar = el('div', 'h-2 rounded-full bg-[#1b120c] border border-[#5a4632] overflow-hidden', {},
      el('div', 'h-full transition-all', {
        style: `width:${pct}%;background:${claimable || claimed ? '#7bc86b' : tone}`,
      }));

    // Reward preview — the pixel currency/XP icons, not text symbols.
    const rewards = el('div', 'flex items-center gap-2 flex-wrap text-[11px] font-mono text-coin', {},
      ...richText(
        [q.quest.rewardSoft ? `🪙 ${q.quest.rewardSoft.toLocaleString()}` : '',
         q.quest.rewardPremium ? `🍄 ${q.quest.rewardPremium.toLocaleString()}` : '',
         q.quest.rewardXp ? `⭐ ${q.quest.rewardXp.toLocaleString()} XP` : ''].filter(Boolean).join('  '),
        12));

    // 44px min tap target, and the disabled state says WHY (how many are left)
    // rather than a generic "in progress".
    const action = claimed
      ? el('div', 'game-font min-h-[44px] px-3 flex items-center justify-center gap-1.5 text-xs text-leaf rounded-md border-2 border-[#3f5a35] bg-[#1d2a18] w-full sm:w-32',
          {}, gicon('check', 12), 'Claimed')
      : claimable
        ? el('button', 'tb-pixel-btn min-h-[44px] px-4 text-sm w-full sm:w-32 inline-flex items-center justify-center gap-1.5',
            { onClick: () => void claim(q.id) }, gicon('coin', 14), 'Claim')
        : el('button', 'game-font min-h-[44px] px-3 text-xs w-full sm:w-32 rounded-md border-2 border-[#5a4632]/70 bg-[#1b120c] text-text-dim/80 cursor-not-allowed tabular-nums',
            { disabled: true as unknown as string, 'aria-disabled': 'true' }, `${Math.max(0, target - cur)} to go`);

    return el('div', 'flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border-2 bg-[#241810]/70 '
      + (claimable ? 'border-[#7bc86b]/70' : claimed ? 'border-[#5a4632]/50 opacity-70' : 'border-[#5a4632]'),
      {},
      el('div', 'flex items-start gap-3 flex-1 min-w-0', {},
        emblem,
        el('div', 'flex-1 min-w-0 flex flex-col gap-1.5', {},
          el('div', 'flex flex-wrap items-center gap-x-2 gap-y-1', {},
            el('span', 'game-font text-sm text-[#ffe3a6] leading-tight', {}, q.quest.name),
            el('span', 'text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border',
              { style: `color:${tone};border-color:${tone}66` }, DIFFICULTY_LABEL[diff])),
          el('div', 'text-xs text-text-dim leading-snug', {}, q.quest.description),
          bar,
          el('div', 'flex flex-wrap items-center justify-between gap-x-3 gap-y-1', {},
            el('span', 'font-mono text-[11px] text-text-dim tabular-nums', {},
              `${cur.toLocaleString()} / ${target.toLocaleString()}  ·  ${pct}%`),
            rewards))),
      el('div', 'shrink-0 sm:w-32', {}, action));
  }

  // ─── Boards ───────────────────────────────────────────────────────────────
  function paintBoards() {
    clear(boardsHost);
    timers = [];
    if (!quests.length) { boardsHost.append(emptyState()); return; }

    for (const g of groupQuests(quests)) {
      const ready = g.quests.filter(isClaimable).length;
      const done = g.quests.filter((q) => isClaimable(q) || isClaimed(q)).length;

      // Live countdown to this board's reset. Only rendered for types whose reset
      // schedule we actually know (daily/weekly cron).
      const ms = msUntilReset(g.type, Date.now());
      const countdown = ms == null ? null
        : el('span', 'text-[11px] font-mono text-text-dim tabular-nums', {}, `resets in ${fmtCountdown(ms)}`);
      if (countdown) timers.push([countdown, g.type]);

      boardsHost.append(el('section', PANEL, {},
        panelHeader(
          el('span', 'flex-1 min-w-0', {}, g.label),
          el('span', 'text-[11px] font-mono text-text-dim tabular-nums', {}, `${done}/${g.quests.length}`),
          ready ? el('span', 'text-[11px] text-leaf inline-flex items-center gap-1', {}, gicon('check', 10), `${ready} ready`) : null,
          countdown),
        el('div', 'p-3 sm:p-4 flex flex-col gap-3', {}, ...g.quests.map(questRow))));
    }
  }

  /** Empty boards are a real state on a fresh account for the moment before the
   *  server assigns a set — say so with the board's own furniture, not a blank div. */
  function emptyState(): HTMLElement {
    return el('section', PANEL, {},
      panelHeader('Quest board'),
      el('div', 'p-8 flex flex-col items-center gap-3 text-center', {},
        el('div', 'rounded-md flex items-center justify-center', {
          style: 'width:64px;height:64px;background:radial-gradient(circle at 50% 38%, #3a2a1b, #241810);border:2px solid #5a4632',
        }, gicon('chest', 34)),
        el('div', 'game-font text-sm text-[#ffe3a6]', {}, 'The board is bare'),
        el('div', 'text-xs text-text-dim max-w-xs leading-snug', {},
          'No errands are pinned up right now. Dailies go up at midnight UTC and weeklies every Monday — check back then.'),
        el('button', 'tb-pixel-btn min-h-[44px] px-4 text-sm', { onClick: () => void reload() }, 'Refresh')));
  }

  // ─── Claiming ─────────────────────────────────────────────────────────────
  // Keyed per quest: claiming quest A must not block claiming quest B, but
  // claiming A twice must only ever pay out once.
  async function claim(id: string) {
    try {
      await dispatchAction('quest.claim', async (actionId) => {
        const r = await progressionApi.claim(id, actionId);
        playSfx('quest_complete');
        toast(stripEmoji(`Claimed · +${r.soft} coins · +${r.xp} XP`));
        await Promise.all([loadQuests(), ctx.refreshHud()]);
      }, { key: id });
    } catch (e) {
      if (isDropped(e)) return;
      toast(e instanceof ApiError ? e.message : 'Claim failed', 'error');
    }
  }

  async function loadQuests() {
    quests = (await progressionApi.quests()) as unknown as QuestRow[];
    paintSummary();
    paintBoards();
  }

  async function reload() {
    try { await loadQuests(); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Failed to load progression', 'error'); }
  }

  // One interval for the whole screen — a timer per board would drift apart and
  // each would keep the view alive after teardown.
  const tick = window.setInterval(() => {
    for (const [node, type] of timers) {
      const ms = msUntilReset(type, Date.now());
      if (ms != null) node.textContent = `resets in ${fmtCountdown(ms)}`;
    }
  }, 1000);

  void reload();
  return () => window.clearInterval(tick);
}
